import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import suppliersService from '../services/suppliersService';
import { useNotification } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import ConfirmActionModal from '../components/ConfirmActionModal';
import '../styles/general.css';

const cleanPhoneForWhatsApp = (phone) => phone.replace(/\D/g, '');

const SuppliersListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled: a button the user can never
  // enable is its own kind of noise. The API rejects them regardless.
  const canWrite = canManage && canManage('suppliers');

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('-created_at');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [togglingSupplier, setTogglingSupplier] = useState(null);
  const [processing, setProcessing] = useState(false);

  const perPage = 50;
  const totalPages = Math.ceil(totalCount / perPage);

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await suppliersService.listSuppliers({
        filterField: filterField || null,
        filterValue: filterValue || null,
        search: searchValue || null,
        ordering: sortBy,
        page,
        includeInactive: showInactive
      });
      setSuppliers(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, filterField, filterValue, searchValue, showInactive, notify, t]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const handleFilterChange = (field, value) => {
    setFilterField(field);
    setFilterValue(value);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const handleShowInactiveChange = (checked) => {
    setShowInactive(checked);
    setPage(1);
  };

  const handleToggleActiveConfirm = async () => {
    if (!togglingSupplier) return;
    setProcessing(true);
    try {
      await suppliersService.toggleActive(togglingSupplier.id);
      const message = togglingSupplier.is_active ? t('suppliers.supplierDeactivated') : t('suppliers.supplierActivated');
      notify.success(message);
      setTogglingSupplier(null);
      loadSuppliers();
    } catch (err) {
      console.error('Error toggling supplier active state:', err);
      notify.error(t('common.failedToUpdate'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading && suppliers.length === 0) {
    return (
      <div className="loading">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('suppliers.title')}</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/suppliers/create')}
            className="btn btn-primary"
          >
            {t('suppliers.createButton')}
          </button>
        )}
      </div>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label">{t('filters.filterBy')}</label>
            <select
              value={filterField}
              onChange={(e) => handleFilterChange(e.target.value, filterValue)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="name">{t('suppliers.filters.byName')}</option>
              <option value="rfc">{t('suppliers.filters.byRfc')}</option>
              <option value="products">{t('suppliers.filters.byProducts')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">{t('filters.searchValue')}</label>
            <input
              type="text"
              value={filterField ? filterValue : searchValue}
              onChange={(e) => (filterField ? handleFilterChange(filterField, e.target.value) : handleSearchChange(e.target.value))}
              placeholder={t('filters.searchValue')}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label">{t('filters.sortBy')}</label>
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="form-select"
            >
              <option value="-created_at">{t('filters.createdNewest')}</option>
              <option value="created_at">{t('filters.createdOldest')}</option>
              <option value="name">{t('suppliers.filters.nameAZ')}</option>
              <option value="-name">{t('suppliers.filters.nameZA')}</option>
              <option value="rfc">{t('suppliers.filters.rfcAZ')}</option>
              <option value="-rfc">{t('suppliers.filters.rfcZA')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => handleShowInactiveChange(e.target.checked)}
              />
              {' '}{t('filters.showInactive')}
            </label>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('suppliers.name')}</th>
              <th>{t('suppliers.rfc')}</th>
              <th>{t('suppliers.phones')}</th>
              <th>{t('suppliers.products')}</th>
              <th>{t('suppliers.hasCredit')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  <p>{t('suppliers.noSuppliers')}</p>
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td>{supplier.id}</td>
                  <td>{supplier.name}</td>
                  <td>{supplier.rfc || '—'}</td>
                  <td>
                    {supplier.phones.map((phone) => (
                      <div key={phone}>
                        <a
                          href={`https://wa.me/+52${cleanPhoneForWhatsApp(phone)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {phone}
                        </a>
                      </div>
                    ))}
                  </td>
                  <td>
                    {supplier.products.map((product) => (
                      <span key={product} className="tag-badge">{product}</span>
                    ))}
                  </td>
                  <td>{supplier.has_credit ? t('common.yes') : t('common.no')}</td>
                  <td className="table-actions">
                    {canWrite && (
                      <button
                        onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {canWrite && (
                      <button
                        onClick={() => setTogglingSupplier(supplier)}
                        className={`btn btn-small ${supplier.is_active ? 'btn-danger' : 'btn-info'}`}
                      >
                        {supplier.is_active ? t('suppliers.deactivateButton') : t('suppliers.activateButton')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">{t('common.page')} {page}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!togglingSupplier}
        onClose={() => setTogglingSupplier(null)}
        onConfirm={handleToggleActiveConfirm}
        title={togglingSupplier?.is_active ? t('suppliers.deactivateButton') : t('suppliers.activateButton')}
        message={togglingSupplier?.is_active ? t('suppliers.confirmDeactivate') : t('suppliers.confirmActivate')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />
    </div>
  );
};

export default SuppliersListPage;
